import { useCallback, useEffect, useState } from 'react';
import {
  compareCustomers,
  listDuplicates,
  resolveDuplicates,
  scanDuplicates,
  type ComparePayload,
  type DuplicateCandidate,
  type DuplicateKind,
  type DuplicateSide,
} from '../api';
import type { IdentityTranslate } from '../i18n';
import { type IdentityLocale } from '../i18n';
import {
  Button,
  Card,
  DataTable,
  Drawer,
  Field,
  Id,
  Notice,
  TextInput,
  type Column,
  Checkbox,
} from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import { ComparePanel } from '../merge/compare-panel';
import { errorText } from '../../../shell/errors';

const AUTOMATABLE: readonly DuplicateKind[] = ['same_phone', 'same_vehicle', 'same_name_vehicle'];

function evidenceText(candidate: DuplicateCandidate): string {
  const e = candidate.evidence;
  if (!e) return '—';
  if (candidate.kind === 'same_phone') return String(e['matched_key'] ?? '—');
  if (candidate.kind === 'same_vehicle') return String(e['plate'] ?? '—');
  if (candidate.kind === 'similar_plate') {
    return `${String(e['left_plate'] ?? '?')} ↔ ${String(e['right_plate'] ?? '?')}`;
  }
  return `${String(e['name_key'] ?? '?')} · ${String(e['signal'] ?? '')}`;
}

export function DuplicatesView({
  t,
  locale,
  humanOnly,
  siteId,
}: {
  t: IdentityTranslate;
  locale: IdentityLocale;
  humanOnly: boolean;
  siteId: string | null;
}) {
  const toast = useToast();
  const [rows, setRows] = useState<readonly DuplicateCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [comparison, setComparison] = useState<ComparePayload | null>(null);

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [locale, toast],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const kinds = humanOnly ? (['similar_plate'] as const) : AUTOMATABLE;
      const pages = await Promise.all(
        kinds.map((kind) => listDuplicates(kind, siteId ?? undefined)),
      );
      setRows(pages.flatMap((p) => p.candidates));
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [humanOnly, siteId, fail]);

  useEffect(() => {
    void load();
    setSelected(new Set());
    setComparison(null);
  }, [load]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const run = async (action: 'merge' | 'reject'): Promise<void> => {
    // Раньше на оба случая — «не выбрана пара» и «не указано основание» — выдавалось
    // одно и то же «Заполните обязательные поля».
    if (selected.size === 0) {
      toast.warn(t('identity.duplicates.needSelection'));
      return;
    }
    if (reason.trim().length < 3) {
      toast.warn(t('identity.duplicates.needReason'));
      return;
    }
    setBusy(true);
    try {
      const count = selected.size;
      await resolveDuplicates(
        [...selected].map((candidateId) => ({ candidateId, action })),
        reason,
      );
      setSelected(new Set());
      setReason('');
      // Панель сравнения раньше оставалась открытой со старыми данными по паре,
      // которой уже нет в таблице.
      setComparison(null);
      await load();
      toast.ok(
        action === 'merge'
          ? t('identity.duplicates.mergedOk', { n: count })
          : t('identity.duplicates.rejectedOk', { n: count }),
      );
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const rescan = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await scanDuplicates();
      toast.ok(t('identity.duplicates.scanned', { proposed: result.proposed }));
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const compare = async (candidate: DuplicateCandidate): Promise<void> => {
    try {
      setComparison(await compareCustomers(candidate.leftId, candidate.rightId));
    } catch (err) {
      fail(err);
    }
  };

  const columns: ReadonlyArray<Column<DuplicateCandidate>> = [
    {
      key: 'sel',
      header: '',
      width: '2rem',
      render: (row) => <Checkbox checked={selected.has(row.id)} onChange={() => toggle(row.id)} />,
    },
    {
      key: 'kind',
      header: t('identity.duplicates.kind'),
      render: (row) => t(`identity.kind.${row.kind}` as never),
    },
    {
      // В строке должно быть видно, о ком речь. Обрезки ULID вида «JC0EY4VA ↔
      // YDVJ8K2K» этого не отвечали: чтобы понять, кто эти люди, приходилось
      // открывать сравнение по каждой паре.
      key: 'pair',
      header: t('identity.duplicates.pair'),
      render: (row) => (
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
          <SideCard side={row.left} t={t} />
          <span aria-hidden className="text-ink-faint">
            ↔
          </span>
          <SideCard side={row.right} t={t} />
        </div>
      ),
    },
    {
      key: 'evidence',
      header: t('identity.duplicates.evidence'),
      render: (row) => <span className="text-xs">{evidenceText(row)}</span>,
    },
    {
      key: 'score',
      header: t('identity.duplicates.score'),
      numeric: true,
      render: (row) => row.score.toFixed(2),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <Button onClick={() => void compare(row)}>{t('identity.duplicates.compare')}</Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {humanOnly ? <Notice tone="warn">{t('identity.duplicates.humanOnly')}</Notice> : null}

      <Card>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <Button tone="ghost" disabled={busy} onClick={() => void rescan()}>
            {busy ? t('identity.duplicates.scanning') : t('identity.duplicates.scan')}
          </Button>
          <span className="text-sm text-ink-muted">
            {t('identity.duplicates.selected', { n: selected.size })}
          </span>
        </div>

        <DataTable
          loading={loading}
          rows={rows}
          columns={columns}
          empty={t('identity.duplicates.empty')}
          rowKey={(row) => row.id}
        />

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <Field
              label={t('identity.duplicates.reason')}
              hint={t('identity.duplicates.reasonHint')}
              required
            >
              <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </div>
          {humanOnly ? null : (
            <Button tone="primary" disabled={busy} onClick={() => void run('merge')}>
              {t('identity.duplicates.confirm')}
            </Button>
          )}
          <Button tone="danger" disabled={busy} onClick={() => void run('reject')}>
            {t('identity.duplicates.reject')}
          </Button>
        </div>
        <p className="mt-2 text-xs text-ink-muted">{t('identity.duplicates.rejectedOnce')}</p>
      </Card>

      {/* Сравнение выезжает поверх экрана. Раньше панель рисовалась ПОД таблицей,
          за пределами вьюпорта, и выглядело это как «кнопка не сработала». */}
      {comparison ? (
        <Drawer title={t('identity.compare.title')} onClose={() => setComparison(null)}>
          <ComparePanel payload={comparison} t={t} />
        </Drawer>
      ) : null}
    </div>
  );
}

/** Одна сторона пары: имя, маскированный телефон, номера машин, число визитов. */
function SideCard({ side, t }: { side: DuplicateSide; t: IdentityTranslate }) {
  return (
    <div className="min-w-44">
      <div className="font-medium text-ink">
        {side.displayName ?? t('identity.duplicates.noName')}
      </div>
      <div className="text-xs text-ink-muted">
        {side.phoneMasked ?? t('identity.duplicates.noPhone')}
        {side.plates.length > 0 ? ` · ${side.plates.join(', ')}` : ''}
      </div>
      <div className="text-xs text-ink-faint">
        {t('identity.duplicates.visits', { n: side.visits })} · <Id value={side.id} />
      </div>
    </div>
  );
}
